import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge } from '@/components/ui';
import { useIssues } from './hooks/useIssues';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { IssueTable } from './components/IssueTable';
import { IssueFormModal } from './components/IssueFormModal';
import type { MaintenanceIssue, IssueStatus, IssueSeverity } from './types/maintenance-issues.types';

const statusLabels: Record<IssueStatus, string> = {
  open: 'Đang mở',
  assigned: 'Đã giao',
  in_progress: 'Đang xử lý',
  pending_review: 'Chờ duyệt',
  resolved: 'Đã xử lý',
  closed: 'Đã đóng',
  reopened: 'Mở lại',
  escalated: 'Báo cáo cấp trên',
};

const severityLabels: Record<IssueSeverity, string> = {
  low: 'Thấp',
  medium: 'Trung bình',
  high: 'Cao',
  critical: 'Khẩn cấp',
};

export default function IssueListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();
  
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<MaintenanceIssue | null>(null);

  const {
    issues,
    total,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useIssues(companyId);

  return (
    <PageLayout
      title="Quản lý Sự cố (Issues)"
      description="Ghi nhận và xử lý các vấn đề phát sinh tại hiện trường từ cây chết, hỏng hệ thống tưới đến vi phạm quy trình."
      breadcrumbs={[{ label: 'Bảo dưỡng' }, { label: 'Sự cố' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-download-line">Báo cáo sự cố</Button>
          <AuthButton 
            module="maintenance" 
            action="create" 
            variant="primary" 
            icon="ri-add-line" 
            onClick={() => setShowCreate(true)}
          >
            Báo cáo sự cố mới
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Sự cố đang mở"
            value={stats?.openIssues || 0}
            icon="ri-error-warning-line"
            variant="danger"
          />
          <KPICard
            title="Đang xử lý"
            value={stats?.inProgress || 0}
            icon="ri-tools-line"
            variant="warning"
          />
          <KPICard
            title="Vi phạm SLA"
            value={stats?.slaBreaches || 0}
            icon="ri-alarm-warning-line"
            variant="primary"
          />
          <KPICard
            title="Đã xong hôm nay"
            value={stats?.resolvedToday || 0}
            icon="ri-checkbox-circle-line"
            variant="success"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã số, tiêu đề sự cố..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  },
                  {
                    id: 'severity', label: 'Mức độ', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả mức độ' },
                      ...Object.entries(severityLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <IssueTable 
            data={issues} 
            isLoading={isLoading} 
            onEdit={(item) => setEditItem(item)}
            onRowClick={(item) => navigate(`./detail/${item.id}`)} 
          />
        </Card>
      </div>

      <IssueFormModal
        isOpen={showCreate || !!editItem}
        item={editItem}
        companyId={companyId}
        onClose={() => {
          setShowCreate(false);
          setEditItem(null);
        }}
      />
    </PageLayout>
  );
}
